import React, { useEffect, useMemo, useState } from "react";
import "./Users.css";

import UserDetailsModal from "../components/users/UserDetailsModal";
import { db } from "../firebase/firebase";
import { collection, getDocs, deleteDoc, doc } from "firebase/firestore";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [verificationFilter, setVerificationFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);

  // ✅ evita clique duplo no remover
  const [deletingIds, setDeletingIds] = useState(() => new Set());

  function setDeleting(userId, value) {
    setDeletingIds((prev) => {
      const next = new Set(prev);
      if (value) next.add(userId);
      else next.delete(userId);
      return next;
    });
  }

  function isDeleting(userId) {
    return deletingIds.has(userId);
  }

  function calculateAge(birthDate) {
    if (!birthDate) return null;

    try {
      // Timestamp (Firestore)
      if (birthDate?.seconds) {
        const d = new Date(birthDate.seconds * 1000);
        if (isNaN(d.getTime())) return null;
        const today = new Date();
        let age = today.getFullYear() - d.getFullYear();
        const m = today.getMonth() - d.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
        return age;
      }

      // string / Date
      const d = new Date(birthDate);
      if (isNaN(d.getTime())) return null;

      const today = new Date();
      let age = today.getFullYear() - d.getFullYear();
      const m = today.getMonth() - d.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
      return age;
    } catch (_) {
      return null;
    }
  }

  useEffect(() => {
    async function loadUsers() {
      try {
        const snapshot = await getDocs(collection(db, "users"));

        const data = snapshot.docs.map((docSnap) => {
          const u = docSnap.data() || {};

          // ✅ PRIORIDADE (mobile): avatarUrls
          // ✅ COMPAT (legado): albumPhotos / photos / album
          const rawPhotos =
            u.avatarUrls ?? u.albumPhotos ?? u.photos ?? u.album ?? [];

          const avatarUrls = Array.isArray(rawPhotos)
            ? rawPhotos.map((x) => String(x))
            : [];

          const photoUrl =
            avatarUrls?.length > 0
              ? avatarUrls[0]
              : (u.photoUrl ? String(u.photoUrl) : null);

          return {
            id: docSnap.id,

            // básicos
            name: u.name || "Sem nome",
            email: u.email || "Sem email",
            city: u.city || "-",
            gender: u.gender || "-",
            plan: u.plan || "free",
            verificationStatus: u.verificationStatus || "none",
            isBanned: u.isBanned || false,

            // datas
            birthDate: u.birthDate || null,
            createdAt: u.createdAt || null,
            lastLogin: u.lastLogin || u.lastActiveAt || null,

            // UI
            age: calculateAge(u.birthDate),
            photoUrl,

            // ✅ IMPORTANTÍSSIMO: mandar o álbum para o modal
            avatarUrls,

            // preferências (compat)
            lifestyleTags: u.lifestyleTags || [],
            values:
              u.values || u.valueTags || u.valuesTags || u.valores || [],
            preferences:
              u.preferences || u.preferencias || u.relationshipPreferences || [],
            dealbreakers:
              u.dealbreakers || u.naoNegociaveis || u.nonNegotiables || [],
            bio: u.bio || "",
          };
        });

        setUsers(data);
      } catch (e) {
        console.error("Erro ao carregar usuários:", e);
      }
      setLoading(false);
    }

    loadUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const term = searchTerm.toLowerCase().trim();
      if (term) {
        const inName = user.name.toLowerCase().includes(term);
        const inEmail = user.email.toLowerCase().includes(term);
        const inCity = user.city.toLowerCase().includes(term);
        if (!inName && !inEmail && !inCity) return false;
      }

      if (planFilter !== "all" && user.plan !== planFilter) return false;

      if (
        verificationFilter !== "all" &&
        user.verificationStatus !== verificationFilter
      )
        return false;

      if (statusFilter === "active" && user.isBanned) return false;
      if (statusFilter === "banned" && !user.isBanned) return false;

      return true;
    });
  }, [users, searchTerm, planFilter, verificationFilter, statusFilter]);

  function handleOpenUser(user) {
    setSelectedUser(user);
  }

  function handleCloseModal() {
    setSelectedUser(null);
  }

  function handleVerificationChange(userId, newStatus) {
    setUsers((prev) =>
      prev.map((u) =>
        u.id === userId ? { ...u, verificationStatus: newStatus } : u
      )
    );

    // mantém o modal sincronizado
    setSelectedUser((prev) =>
      prev?.id === userId ? { ...prev, verificationStatus: newStatus } : prev
    );
  }

  function handleBanToggle(userId) {
    setUsers((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, isBanned: !u.isBanned } : u))
    );

    // mantém o modal sincronizado
    setSelectedUser((prev) =>
      prev?.id === userId ? { ...prev, isBanned: !prev.isBanned } : prev
    );
  }

  // ✅ remover usuário (Firestore + estado)
  async function handleRemoveUser(user) {
    if (!user?.id) return;
    if (isDeleting(user.id)) return;

    const ok1 = window.confirm(
      `Tem certeza que deseja REMOVER o usuário?\n\n${user.name} (${user.email})\n\nIsso apaga o documento em Firestore e não pode ser desfeito.`
    );
    if (!ok1) return;

    const typed = window.prompt("Para confirmar, digite: REMOVER");
    if ((typed || "").trim().toUpperCase() !== "REMOVER") {
      alert("Remoção cancelada.");
      return;
    }

    setDeleting(user.id, true);
    try {
      await deleteDoc(doc(db, "users", user.id));

      // remove da lista local
      setUsers((prev) => prev.filter((u) => u.id !== user.id));

      // se estava no modal aberto, fecha
      setSelectedUser((prev) => (prev?.id === user.id ? null : prev));

      alert("✅ Usuário removido com sucesso!");
    } catch (e) {
      console.error("Erro ao remover usuário:", e);
      alert("❌ Erro ao remover usuário. Verifique permissões/regras do Firestore.");
    } finally {
      setDeleting(user.id, false);
    }
  }

  return (
    <div className="users-page">
      {/* HEADER */}
      <div className="users-header">
        <div className="header-left">
          <h1 className="users-title">Usuários 👥</h1>
          <p className="users-subtitle">
            Gestão dos perfis cadastrados, planos e verificação de contas.
          </p>
        </div>

        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-label">TOTAL</div>
            <div className="stat-value">{users.length}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">VERIFICADOS</div>
            <div className="stat-value">
              {users.filter((u) => u.verificationStatus === "verified").length}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">PENDENTES</div>
            <div className="stat-value">
              {users.filter((u) => u.verificationStatus === "pending").length}
            </div>
          </div>
        </div>
      </div>

      {/* SEARCH */}
      <div className="search-box">
        <input
          type="text"
          className="search-input"
          placeholder="Buscar por nome, e-mail ou cidade..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* FILTERS */}
      <div className="filters-container">
        <div className="filter-group">
          <div className="filter-label">PLANO</div>
          <select
            className="filter-select"
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
          >
            <option value="all">Todos</option>
            <option value="free">Free</option>
            <option value="premium">Premium</option>
            <option value="vip">VIP</option>
            <option value="diamond">Diamond</option>
          </select>
        </div>

        <div className="filter-group">
          <div className="filter-label">VERIFICAÇÃO</div>
          <select
            className="filter-select"
            value={verificationFilter}
            onChange={(e) => setVerificationFilter(e.target.value)}
          >
            <option value="all">Todas</option>
            <option value="verified">Verificado</option>
            <option value="pending">Pendente</option>
            <option value="none">Não solicitado</option>
          </select>
        </div>

        <div className="filter-group">
          <div className="filter-label">STATUS</div>
          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Todos</option>
            <option value="active">Ativo</option>
            <option value="banned">Banido</option>
          </select>
        </div>
      </div>

      {/* TABLE */}
      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Carregando usuários...</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th>PERFIL</th>
                <th>IDADE</th>
                <th>PLANO</th>
                <th>CIDADE</th>
                <th>GÊNERO</th>
                <th>VERIFICAÇÃO</th>
                <th>STATUS</th>
                <th>AÇÕES</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="8" className="empty-state">
                    <div className="empty-icon">📭</div>
                    <div>Nenhum usuário encontrado</div>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => {
                  const deleting = isDeleting(user.id);

                  return (
                    <tr
                      key={user.id}
                      className={user.isBanned ? "user-banned" : ""}
                    >
                      <td>
                        <div className="user-cell">
                          <div className="user-avatar">
                            {user.photoUrl ? (
                              <img src={user.photoUrl} alt={user.name} />
                            ) : (
                              <span>{user.name.charAt(0)}</span>
                            )}
                          </div>
                          <div className="user-info">
                            <div className="user-name">{user.name}</div>
                            <div className="user-email">{user.email}</div>
                          </div>
                        </div>
                      </td>

                      <td>{user.age || "-"}</td>

                      <td>
                        <span className={`badge badge-plan badge-${user.plan}`}>
                          {user.plan === "free"
                            ? "Free"
                            : user.plan === "premium"
                            ? "Premium"
                            : user.plan === "diamond"
                            ? "Diamond"
                            : "VIP"}
                        </span>
                      </td>

                      <td>{user.city}</td>
                      <td>{user.gender}</td>

                      <td>
                        <span
                          className={`badge badge-verification badge-${user.verificationStatus}`}
                        >
                          {user.verificationStatus === "verified"
                            ? "Verificado"
                            : user.verificationStatus === "pending"
                            ? "Pendente"
                            : "Não solicitado"}
                        </span>
                      </td>

                      <td>
                        <span
                          className={`badge badge-status badge-${
                            user.isBanned ? "banned" : "active"
                          }`}
                        >
                          {user.isBanned ? "Banido" : "Ativo"}
                        </span>
                      </td>

                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-view"
                            onClick={() => handleOpenUser(user)}
                            disabled={deleting}
                          >
                            Ver detalhes
                          </button>

                          {user.verificationStatus === "verified" ? (
                            <button
                              className="btn btn-revoke"
                              onClick={() =>
                                handleVerificationChange(user.id, "none")
                              }
                              disabled={deleting}
                            >
                              Revogar
                            </button>
                          ) : (
                            <button
                              className="btn btn-verify"
                              onClick={() =>
                                handleVerificationChange(user.id, "verified")
                              }
                              disabled={deleting}
                            >
                              Verificar
                            </button>
                          )}

                          <button
                            className={`btn ${
                              user.isBanned ? "btn-unban" : "btn-ban"
                            }`}
                            onClick={() => handleBanToggle(user.id)}
                            disabled={deleting}
                          >
                            {user.isBanned ? "Desbanir" : "Banir"}
                          </button>

                          <button
                            className="btn btn-delete"
                            onClick={() => handleRemoveUser(user)}
                            disabled={deleting}
                            title="Remove o usuário do Firestore"
                          >
                            {deleting ? "Removendo..." : "Remover"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          onClose={handleCloseModal}
          onChangeVerification={handleVerificationChange}
          onToggleBan={handleBanToggle}
        />
      )}
    </div>
  );
}
